import { legacyKyInstance } from '@/lib/api-client'
import { AddCartItem, Cart, CartItemLegacy } from '@/types/commerce-types'
import { v4 as uuidv4 } from 'uuid'
import { logger } from '../../lib/logger'

interface PaymentLegacy {
  Success: boolean
  PublicId?: string

  CartKey?: string
  Message?: string
  TransactionStatus?: string
  PaymentType?: string
  Total: number
  OrderViewUrl: string
}

/**
 * Fetch cart data from the legacy API
 * This is the same endpoint used by legacy HTML pages (checkout-cart.js, etc.)
 * @param cartKey - The cart key from localStorage['mycart']
 * @returns Cart data compatible with Cart interface
 */
export const fetchCartLegacy = async (cartKey: string): Promise<Cart> => {
  try {
    logger.info('Fetching cart from legacy API', { cartKey })
    const res = await legacyKyInstance.get<Cart>(`Cart?key=${cartKey}`).json()
    logger.info('Cart fetched from legacy API', { cartKey, items: res?.items?.length ?? 0 })
    return res
  } catch (error) {
    logger.error('Error fetching cart from legacy API', { cartKey, error })
    throw new Error('Failed to fetch cart', { cause: error })
  }
}

export const fetchCartKeyLegacy = async (): Promise<string> => {
  try {
    const res = await legacyKyInstance.get<string>(`Cart?newCart=${uuidv4()}`).json()
    return res
  } catch (error) {
    console.error('Error fetching cart key:', error)
    throw new Error('Failed to fetch cart key', { cause: error })
  }
}

export const addCartItemLegacy = async (item: AddCartItem): Promise<CartItemLegacy[]> => {
  try {
    logger.info('Adding item to cart', { item })
    const response = await legacyKyInstance.post<CartItemLegacy[]>('cart', { json: item }).json()
    return response
  } catch (error) {
    console.error(`Error adding item to cart: ${item.cartKey}`, error)
    throw new Error('Failed to add item to cart', { cause: error })
  }
}

export const removeCartItemLegacy = async (cartKey: string, itemId: number): Promise<boolean> => {
  try {
    logger.info('Removing item from cart', { cartKey, itemId })
    await legacyKyInstance.delete('cart', { json: { id: itemId, key: cartKey } })
    return true
  } catch (error) {
    console.error(`Error removing item from cart: ${cartKey}`, error)
    throw new Error('Failed to remove item from cart', { cause: error })
  }
}

export const postPaymentLegacy = async (email: string, cartKey: string): Promise<PaymentLegacy> => {
  try {
    const payload = {
      email: email,
      key: cartKey,
      isFinal: true,
    }
    const req = await legacyKyInstance.put<PaymentLegacy>('cart', { json: payload, timeout: 60000 }).json()
    return req
  } catch (error) {
    console.error('Error posting payment:', error)
    throw new Error('Failed to post payment', { cause: error })
  }
}
